import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useTooltip from '../../../../primitives/tooltip/hooks/useTooltip';
import TooltipContext from './TooltipContext';
import { jsx as _jsx } from "react/jsx-runtime";
function isValidNumber(_) {
  return _ != null && typeof _ === 'number' && !Number.isNaN(_) && Number.isFinite(_);
}
/** Simple wrapper around useTooltip, to provide tooltip data via context. */
export default function TooltipProvider(_ref) {
  let {
    hideTooltipDebounceMs = 200,
    children
  } = _ref;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    updateTooltip,
    hideTooltip: privateHideTooltip
  } = useTooltip(undefined);
  const debouncedHideTooltip = useRef(null);
  function cancelDeboundedHideTooltip() {
    if (debouncedHideTooltip.current) {
      debouncedHideTooltip.current.cancel();
      debouncedHideTooltip.current = null;
    }
  }
  useEffect(() => cancelDeboundedHideTooltip, []);
  const showTooltip = useRef(props => {
    // cancel any hideTooltip calls so it won't hide after invoking the logic below

    if (!props) return;
    const {
      svgPoint,
      event
    } = props;
    // Datum by key will only be there sometimes

    if (!props.eventData) return;
    const {
      datumByKey,
      ...rest
    } = props === null || props === void 0 ? void 0 : props.eventData;
    cancelDeboundedHideTooltip();
    const tooltipLeft = event && 'clientX' in event ? event === null || event === void 0 ? void 0 : event.clientX : svgPoint === null || svgPoint === void 0 ? void 0 : svgPoint.x;
    const tooltipTop = event && 'clientY' in event ? event === null || event === void 0 ? void 0 : event.clientY : svgPoint === null || svgPoint === void 0 ? void 0 : svgPoint.y;

    // We may want to avoid updates in some cases.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateTooltip(_ref2 => {
      let {
        tooltipData: oldData
      } = _ref2;
      return {
        tooltipOpen: true,
        tooltipLeft,
        tooltipTop,
        tooltipData: {
          event,
          nearestDatum: rest,
          datumByKey: datumByKey || new Map()
        }
      };
    });
  });
  const hideTooltip = useCallback(() => {
    debouncedHideTooltip.current = debounce(privateHideTooltip, hideTooltipDebounceMs);
    debouncedHideTooltip.current();
  }, [privateHideTooltip, hideTooltipDebounceMs]);
  const value = useMemo(() => ({
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    updateTooltip,
    showTooltip: showTooltip.current,
    hideTooltip
  }), [hideTooltip, tooltipData, tooltipLeft, tooltipOpen, tooltipTop, updateTooltip]);
  return /*#__PURE__*/_jsx(TooltipContext.Provider, {
    value: value,
    children: children
  });
}